const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * @param {string | Date | undefined} dueDate
 * @param {Date} [now]
 * @returns {Date | null}
 */
export function toDateOnly(dueDate, now = new Date()) {
  if (!dueDate) return null;
  const date = dueDate instanceof Date ? new Date(dueDate) : new Date(dueDate);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * @param {string | Date | undefined} dueDate
 * @param {Date} [now]
 * @returns {boolean}
 */
export function isCommitmentOverdue(dueDate, now = new Date()) {
  const due = toDateOnly(dueDate, now);
  const today = toDateOnly(now, now);
  if (!due || !today) return false;
  return due.getTime() < today.getTime();
}

/**
 * @param {string | Date | undefined} dueDate
 * @param {Date} [now]
 * @param {number} [days]
 * @returns {boolean}
 */
export function isCommitmentDueSoon(dueDate, now = new Date(), days = 7) {
  const due = toDateOnly(dueDate, now);
  const today = toDateOnly(now, now);
  if (!due || !today) return false;
  if (due.getTime() < today.getTime()) return false;
  const limit = new Date(today.getTime() + days * MS_PER_DAY);
  return due.getTime() <= limit.getTime();
}

/**
 * @param {Date} [now]
 * @returns {{ start: Date, end: Date }}
 */
export function getCurrentWeekRange(now = new Date()) {
  const today = toDateOnly(now, now);
  if (!today) {
    return { start: now, end: now };
  }
  const day = today.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const start = new Date(today.getTime() + mondayOffset * MS_PER_DAY);
  const end = new Date(start.getTime() + 6 * MS_PER_DAY);
  return { start, end };
}

/**
 * ISO-style calendar week number for overview subline.
 * @param {Date} [now]
 */
export function getCalendarWeekNumber(now = new Date()) {
  const date = new Date(now);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - week1.getTime()) / MS_PER_DAY - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

/**
 * @param {Date} [now]
 * @param {(key: string, params?: object) => string} t
 */
export function formatOverviewWeekSubline(now, t) {
  const week = getCalendarWeekNumber(now);
  const { start, end } = getCurrentWeekRange(now);
  const year = now.getFullYear();
  const fmt = (d) => d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' });
  return t('UserProjects.overview.myCommitments.weekSubline', {
    week,
    start: fmt(start),
    end: fmt(end),
    year,
  });
}

/**
 * @param {string | Date | undefined} dueDate
 * @param {Date} [now]
 * @returns {boolean}
 */
export function isCommitmentDueThisWeek(dueDate, now = new Date()) {
  const due = toDateOnly(dueDate, now);
  if (!due) return false;
  const { start, end } = getCurrentWeekRange(now);
  return due.getTime() >= start.getTime() && due.getTime() <= end.getTime();
}

/**
 * @param {Array<{ dueDate?: string | Date, isOverdue?: boolean }>} items
 * @param {Date} [now]
 * @returns {Array}
 */
export function filterCommitmentsThisWeek(items, now = new Date()) {
  return (items || []).filter((item) => isCommitmentDueThisWeek(item.dueDate, now));
}

/**
 * @param {Array<{ dueDate?: string | Date, isOverdue?: boolean }>} items
 * @param {Date} [now]
 * @returns {Array}
 */
export function filterCommitmentsOverdue(items, now = new Date()) {
  return (items || []).filter((item) => item.isOverdue || isCommitmentOverdue(item.dueDate, now));
}

/**
 * @param {Array<{ id: string, dueDate?: string | Date, was?: string }>} commitments
 * @returns {{ id: string, was: string, dueDate: string | Date } | null}
 */
function findNextDue(commitments) {
  const sorted = [...commitments]
    .filter((item) => item.dueDate)
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  if (sorted.length === 0) return null;
  const next = sorted[0];
  return {
    id: next.id,
    was: next.was || '',
    dueDate: next.dueDate,
  };
}

const EMPTY_OVERVIEW = () => ({
  totals: {
    projectCount: 0,
    myOpenCount: 0,
    myOverdueCount: 0,
    myDueSoonCount: 0,
    projectOpenCount: 0,
    projectOverdueCount: 0,
    internalOpenCount: 0,
    crossOpenCount: 0,
    openDecisionsCount: 0,
    overdueDecisionsCount: 0,
    completedMilestonesCount: 0,
    totalMilestonesCount: 0,
    overdueMilestonesCount: 0,
  },
  projectSummaries: [],
  upcomingCommitments: [],
  myOpenCommitments: [],
  activeConstraints: [],
  nextRequiredDecision: null,
  milestones: [],
  nextMilestone: null,
});

/**
 * @param {{
 *   projects?: Array<{ id: string, name: string, status?: string, startDate?: string | Date, endDate?: string | Date }>,
 *   commitmentsByProject?: Record<string, Array<{ id: string, ownerUserId?: string, status?: string, was?: string, dueDate?: string | Date }>>,
 *   constraintsByProject?: Record<string, Array<{ id: string, status?: string, type?: string, description?: string, dueDate?: string | Date, category?: string }>>,
 *   decisionsByProject?: Record<string, Array<{ id: string, status?: string, title?: string, dueDate?: string | Date, decisionOwnerUserId?: string, impactIfDelayed?: string, isOverdue?: boolean, escalationLevel?: number, milestoneId?: string }>>,
 *   milestonesByProject?: Record<string, Array<{ id: string, title?: string, dueDate?: string | Date, actualDate?: string | Date, status?: string, deviationDays?: number, isOverdue?: boolean }>>,
 *   userId?: string,
 *   now?: Date,
 * }} params
 */
export function buildUserProjectsOverview({
  projects = [],
  commitmentsByProject = {},
  constraintsByProject = {},
  decisionsByProject = {},
  milestonesByProject = {},
  userId = '',
  now = new Date(),
} = {}) {
  if (!userId) return EMPTY_OVERVIEW();

  const normalizedUserId = String(userId);
  const allMyOpen = [];
  let projectOpenCount = 0;
  let projectOverdueCount = 0;

  const projectSummaries = (projects || []).map((project) => {
    const projectId = String(project.id);
    const rawItems = commitmentsByProject[projectId] || [];
    const projectOpen = rawItems.filter(
      (item) => item.status === 'open' || item.status === 'in_progress',
    );
    projectOpenCount += projectOpen.length;
    projectOverdueCount += projectOpen.filter(
      (item) => item.isOverdue ?? isCommitmentOverdue(item.dueDate, now),
    ).length;

    const myOpen = projectOpen.filter(
      (item) => String(item.ownerUserId) === normalizedUserId,
    );

    myOpen.forEach((item) => {
      allMyOpen.push({
        ...item,
        projectId,
        projectName: project.name,
        isOverdue: item.isOverdue ?? isCommitmentOverdue(item.dueDate, now),
      });
    });

    return {
      projectId,
      projectName: project.name,
      status: project.status || 'active',
      startDate: project.startDate || null,
      endDate: project.endDate || null,
      myOpenCount: myOpen.length,
      nextDue: findNextDue(myOpen),
    };
  });

  const myOverdueCount = allMyOpen.filter((item) => item.isOverdue).length;
  const myDueSoonCount = allMyOpen.filter(
    (item) => !item.isOverdue && isCommitmentDueSoon(item.dueDate, now),
  ).length;

  const myOpenCommitments = [...allMyOpen]
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
    .map((item) => ({
      id: item.id,
      was: item.was || '',
      dueDate: item.dueDate,
      projectId: item.projectId,
      projectName: item.projectName,
      status: item.status,
      isOverdue: item.isOverdue,
      milestoneTitle: item.milestoneTitle || null,
      ownerDisplayName: item.ownerDisplayName || null,
    }));

  const upcomingCommitments = myOpenCommitments.slice(0, 5);

  const allOpenConstraints = (projects || []).flatMap((project) => {
    const projectId = String(project.id);
    const rawItems = constraintsByProject[projectId] || [];
    return rawItems
      .filter((item) => item.status === 'open' || item.status === 'in_progress')
      .map((item) => ({
        id: item.id,
        title: item.title || '',
        description: item.description || '',
        dueDate: item.dueDate,
        priority: item.priority || null,
        category: item.category || '',
        type: item.type || '',
        daysOpen: item.daysOpen ?? null,
        ownerDisplayName: item.ownerDisplayName || null,
        projectId,
        projectName: project.name,
        status: item.status,
      }));
  });

  const internalOpenCount = allOpenConstraints.filter((item) => item.type === 'intern').length;
  const crossOpenCount = allOpenConstraints.filter(
    (item) => item.type === 'bereichsuebergreifend',
  ).length;

  const activeConstraints = [...allOpenConstraints].sort(
    (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
  );

  const PENDING_DECISION_STATUSES = new Set(['open', 'prepared', 'escalated']);

  const allPendingDecisions = (projects || []).flatMap((project) => {
    const projectId = String(project.id);
    const rawItems = decisionsByProject[projectId] || [];
    return rawItems
      .filter((item) => PENDING_DECISION_STATUSES.has(item.status))
      .map((item) => ({
        id: item.id,
        title: item.title || '',
        dueDate: item.dueDate,
        decisionOwnerUserId: item.decisionOwnerUserId || '',
        decisionOwnerDisplayName: item.decisionOwnerDisplayName || null,
        impactIfDelayed: item.impactIfDelayed || '',
        basis: item.basis || '',
        milestoneId: item.milestoneId || '',
        milestoneTitle: item.milestoneTitle || null,
        status: item.status,
        escalationLevel: item.escalationLevel ?? 0,
        isOverdue: item.isOverdue ?? isCommitmentOverdue(item.dueDate, now),
        projectId,
        projectName: project.name,
      }));
  });

  const openDecisionsCount = allPendingDecisions.length;
  const overdueDecisionsCount = allPendingDecisions.filter((item) => item.isOverdue).length;

  const nextRequiredDecision = [...allPendingDecisions]
    .sort((a, b) => {
      if (a.isOverdue !== b.isOverdue) {
        return a.isOverdue ? -1 : 1;
      }
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    })[0] ?? null;

  const allMilestones = (projects || []).flatMap((project) => {
    const projectId = String(project.id);
    const rawItems = milestonesByProject[projectId] || [];
    return rawItems.map((item) => ({
      id: item.id,
      title: item.title || '',
      dueDate: item.dueDate,
      actualDate: item.actualDate || null,
      status: item.status || 'open',
      deviationDays: item.deviationDays ?? null,
      forecastHint: item.forecastHint ?? null,
      isOverdue: item.isOverdue ?? (
        item.status !== 'done' && isCommitmentOverdue(item.dueDate, now)
      ),
      projectId,
      projectName: project.name,
    }));
  });

  const completedMilestonesCount = allMilestones.filter((item) => item.status === 'done').length;
  const totalMilestonesCount = allMilestones.length;
  const overdueMilestonesCount = allMilestones.filter(
    (item) => item.status !== 'done' && item.isOverdue,
  ).length;

  const milestones = [...allMilestones].sort(
    (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
  );

  const nextMilestone = [...allMilestones]
    .filter((item) => item.status !== 'done')
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())[0] ?? null;

  return {
    totals: {
      projectCount: projectSummaries.length,
      myOpenCount: allMyOpen.length,
      myOverdueCount,
      myDueSoonCount,
      projectOpenCount,
      projectOverdueCount,
      internalOpenCount,
      crossOpenCount,
      openDecisionsCount,
      overdueDecisionsCount,
      completedMilestonesCount,
      totalMilestonesCount,
      overdueMilestonesCount,
    },
    projectSummaries,
    upcomingCommitments,
    myOpenCommitments,
    activeConstraints,
    nextRequiredDecision,
    milestones,
    nextMilestone,
  };
}
